import { format } from 'date-fns'
import { AiFillFlag } from 'react-icons/ai'
import {
    ColumnHeaderDateRangeFilter,
    ColumnHeaderFilter,
    ColumnHeaderReactSelectFilter,
} from '../../../../../components/_shared/ColumnHeaderFilter'
import ThemedTabLink from '../../../../../components/_shared/ThemedTabLink/ThemedTabLink'

const renderHeader = (
    title,
    filterKey,
    columnFilters,
    onColumnFilterChange,
    className = '',
    style = {}
) =>
    onColumnFilterChange ? (
        <ColumnHeaderFilter
            title={title}
            filterKey={filterKey}
            value={columnFilters?.[filterKey]}
            onChange={onColumnFilterChange}
            className={className}
            style={style}
        />
    ) : (
        <span className={className} style={style} title={title}>
            {title}
        </span>
    )

const renderStatusHeader = (
    title,
    filterKey,
    columnFilters,
    onColumnFilterChange,
    userOptions = [],
    className = '',
    style = {}
) =>
    onColumnFilterChange ? (
        <ColumnHeaderReactSelectFilter
            title={title}
            filterKey={filterKey}
            value={columnFilters?.[filterKey]}
            onChange={onColumnFilterChange}
            options={userOptions}
            className={className}
            style={style}
            menuMinWidth={240}
        />
    ) : (
        <span className={className} style={style} title={title}>
            {title}
        </span>
    )

const renderDateRangeHeader = (
    title,
    columnFilters,
    onColumnFilterChange,
    className = '',
    style = {}
) =>
    onColumnFilterChange ? (
        <ColumnHeaderDateRangeFilter
            title={title}
            startFilterKey="startincident_dt"
            endFilterKey="endincident_dt"
            startValue={columnFilters?.startincident_dt}
            endValue={columnFilters?.endincident_dt}
            onChange={(key, value) => {
                onColumnFilterChange('startincident_dt', value)
                onColumnFilterChange('endincident_dt', value)
            }}
            singleInput
            className={className}
            style={style}
        />
    ) : (
        <span className={className} style={style} title={title}>
            {title}
        </span>
    )

const columns = (location, columnFilters = {}, onColumnFilterChange, statusOptions = []) => [
    {
        name: renderHeader('ID', 'incident_id', columnFilters, onColumnFilterChange),
        selector: (row) => row?.incident_id,
        sortable: true,
        sortField: 'incident_id',
        cell: (row) => {
            const isLocal = location?.pathname === '/incidents/localincidents'
            const href = isLocal
                ? `/incidents/editincident/${row?.incident_id}`
                : `/myincidents/editmyincident/${row?.incident_id}`

            // ThemedTabLink, not a plain target="_blank" anchor: the browser
            // shows its own white surface until the destination paints, which
            // is a visible flash in dark mode. This applies to My Incidents as
            // much as Local Incidents - the previous fix only covered the
            // isLocal branch, which is why the flash survived it.
            return (
                <ThemedTabLink to={href}>{row?.incident_id}</ThemedTabLink>
            )
        },
        width: '8%',
    },
    {
        name: renderHeader('Incident Name', 'incidentname_tx', columnFilters, onColumnFilterChange),
        cell: (row) => (row?.incidentname_tx || '').slice(0, 50),
        sortable: true,
        sortField: 'incidentname_tx',
        width: '15%',
    },
    {
        name:

            location?.pathname === '/myincidents/myincidentlisting'
                ? 'Description'
                : renderHeader('Description', 'description_tx', columnFilters, onColumnFilterChange),
        cell: (row) => (row?.description_tx || '').slice(0, 50),
        sortable: true,
        width: '14%',
        sortField: 'description_tx',
    },
    {
        name: renderHeader('Score', 'score_nr', columnFilters, onColumnFilterChange),
        cell: (row) => {
            const isLocal = location?.pathname === '/incidents/localincidents'
            const editUrl = isLocal
                ? `/incidents/editincident/${row?.incident_id}`
                : `/myincidents/editmyincident/${row?.incident_id}`;

            let flagColor = '';
            switch (row?.ref_level_display_cd) {
                case 'GREEN':
                    flagColor = 'green';
                    break;
                case 'ORANGE':
                    flagColor = 'orange';
                    break;
                case 'RED':
                    flagColor = 'red';
                    break;
                case 'GRAY':
                    flagColor = 'gray';
                    break;
                default:
                    break;
            }

            return (
                <ThemedTabLink to={editUrl}>
                    <p className="flex items-center gap-1">
                        {row?.ref_level_display_cd && (
                            <AiFillFlag size={22} fill={flagColor} />
                        )}
                        {row?.score_nr !== undefined ? Math.round(row?.score_nr) : ''}
                    </p>
                </ThemedTabLink>
            );
        },
        sortable: true,
        width: '10%',
        sortField: 'score_nr',
    },
    {
        name: renderStatusHeader('Status', 'status', columnFilters, onColumnFilterChange, statusOptions),
        selector: (row) => row?.ref_status_description_tx,
        sortable: true,
        width: '10%',
        sortField: 'ref_status_description_tx',
    },
    {
        name: renderHeader('User', 'user_name_tx', columnFilters, onColumnFilterChange),
        selector: (row) => row?.user_name_tx,
        sortable: true,
        width: '10%',
        sortField: 'user_name_tx',
    },
    {
        name: renderHeader('Device', 'device_name_tx', columnFilters, onColumnFilterChange),
        selector: (row) => row?.device_name_tx,
        sortable: true,
        width: '10%',
        sortField: 'device_name_tx',
    },
    {
        name: renderHeader('Congregation', 'wmrc_division', columnFilters, onColumnFilterChange, 'columnHeading',
            { maxWidth: '90px' }),
        selector: (row) => row?.wmrc_division,
        sortable: true,
        sortField: 'wmrc_division',
        width: '13%',
    },
    {
        name: renderDateRangeHeader('Date', columnFilters, onColumnFilterChange),
        selector: (row) =>
            row?.event_dt &&
            format(new Date(row?.event_dt), 'MM-dd-yyyy h:mm a'),
        sortable: true,
        sortField: 'event_dt',
        width: '10%',
    },
]
const mobileColumns = [
    {
        name: 'ID',
        selector: (row) => row?.incident_id,
        sortable: true,
        sortField: 'incident_id',
        cell: (row) => {
            return (
                <ThemedTabLink
                    to={`/incidents/editincident/${row?.incident_id}/${row?.user_id}`}
                >
                    {row?.incident_id}
                </ThemedTabLink>
            )
        },
    },
    {
        name: 'Incident Name',
        selector: (row) => row?.incidentname_tx,
        sortable: true,
        sortField: 'incidentname_tx',
    },
]

export { columns, mobileColumns }
